/**
 * Route Protection Component
 * Handles authentication state and renders appropriate components
 */

import React from 'react';
import { useAuth, useAuthState } from '../AuthContext';
import { 
  SignIn, 
  PendingAuthentication, 
  PendingAuthorization, 
  AuthError, 
  UnauthorizedUser 
} from './AuthComponents';

interface AuthorizedRouteProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

/**
 * Main route protection component
 * Renders different components based on authentication state
 */
export const AuthorizedRoute = ({ 
  children, 
  fallback 
}: AuthorizedRouteProps): React.ReactElement => {
  const { 
    isAuthenticated, 
    isLoading, 
    unauthorizedUser, 
    user 
  } = useAuth();
  
  const authState = useAuthState();

  // Development bypass for testing without full Auth0 setup
  if (process.env.REACT_APP_DEV_BYPASS_AUTH === 'true') {
    console.warn('🚧 Development bypass enabled - skipping authentication');
    return <>{children}</>;
  }

  // Handle different authentication states
  switch (authState) {
    case 'pending_authentication':
      return isLoading ? <PendingAuthentication /> : <SignIn />;
      
    case 'pending_authorization':
      return <PendingAuthorization />;
      
    case 'unauthorized':
      return <UnauthorizedUser />;
      
    case 'error':
      return <AuthError />;
      
    case 'authenticated':
      // User is fully authenticated and authorized
      return <>{children}</>;
      
    default:
      // Fallback for unknown states
      return (fallback as React.ReactElement) || <PendingAuthentication />;
  }
};

/**
 * Higher-order component for route protection
 * Alternative to using AuthorizedRoute directly
 */
export const withAuthProtection = <P extends object>(
  Component: React.ComponentType<P>
): React.FC<P> => {
  return (props: P) => (
    <AuthorizedRoute>
      <Component {...props} />
    </AuthorizedRoute>
  );
};

/**
 * Component that only renders children if user is authenticated
 * Useful for conditional rendering within components
 */
export const IfAuthenticated: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  
  return isAuthenticated ? <>{children}</> : null;
};

/**
 * Component that only renders children if user is NOT authenticated
 * Useful for showing login-specific content
 */
export const IfNotAuthenticated: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  
  return !isAuthenticated ? <>{children}</> : null;
};